import Player from '../Player';
import { Weapon } from '../Weapon';
import { aimPoint, movementAngle, normalizeAngle } from './World3D';

export interface InputHost {
	enabled(): boolean;
	controllable(): boolean;
	player(): Player | null;
	gameId(): number;
	emit(event: string, ...args: any[]): void;
	setYaw(angle: number | null): void;
}

export default class FirstPersonInput {
	private yaw = 0;
	private playerId: number | null = null;
	private keys = new Set<number>();
	private lastAim = -1;
	private lastAimTime = 0;
	private lastMove: number | null = null;
	private held = false;
	private actionGameId = -1;
	private moveGameId = -1;
	private pressedAt = 0;
	private releaseTimer: number | null = null;
	private locked = false;
	private requestPending = false;
	private captureAllowed = false;
	private starting = false;
	private autoCapturePending = false;
	private captureVersion = 0;
	private originalCursor: string;
	private hint: HTMLDivElement;

	constructor(private layer: HTMLElement, private host: InputHost) {
		this.originalCursor = layer.style.cursor;
		this.hint = document.createElement('div');
		this.hint.textContent = 'Klikni pro ovládání · WASD pohyb · myš otáčení · Esc menu';
		this.hint.style.cssText = 'position:fixed;left:50%;top:35%;transform:translateX(-50%);padding:12px 18px;background:#222c;color:white;border-radius:6px;z-index:8;pointer-events:none;display:none;text-align:center';
		document.body.appendChild(this.hint);
		document.addEventListener('mousemove', this.mouseMove);
		layer.addEventListener('mousedown', this.mouseDown);
		document.addEventListener('mouseup', this.mouseUp);
		document.addEventListener('pointerlockchange', this.lockChange);
		document.addEventListener('pointerlockerror', this.lockError);
		window.addEventListener('blur', this.reset);
		document.addEventListener('visibilitychange', this.visibilityChange);
	}

	update(player: Player, spectating: boolean): void {
		if (!this.host.enabled()) return;
		if (spectating || !player.alive()) {
			this.reset();
			return;
		}
		if (!this.host.controllable()) {
			// A requested start may still be waiting for the first snapshot/loading overlay.
			if (!this.starting) this.reset();
			return;
		}
		this.starting = false;
		if (this.playerId !== player.id) {
			this.yaw = player.getAngle();
			this.playerId = player.id;
		}
		this.host.setYaw(this.yaw);
		if (this.autoCapturePending) {
			this.autoCapturePending = false;
			this.requestCapture();
		}
		this.syncCursor();
		this.hint.style.display = this.isLocked() ? 'none' : 'block';
		if (this.isLocked()) this.sendAim();
	}

	// Called in the Start button gesture, before the asynchronous server response.
	prepareForGame(): void {
		if (!this.host.enabled()) return;
		this.starting = true;
		this.requestCapture();
	}

	startGame(): void {
		this.clearControls();
		this.starting = true;
		this.autoCapturePending = true;
	}

	resume(): void {
		if (!this.host.enabled() || !this.host.controllable()) return;
		const player = this.host.player();
		if (!player || !player.alive()) return;
		this.update(player, false);
		this.requestCapture();
	}

	private requestCapture(): void {
		if (!this.host.enabled() || this.isLocked() || this.requestPending
			|| (!this.starting && !this.host.controllable())) return;
		this.requestPending = true;
		this.captureAllowed = true;
		const version = ++this.captureVersion;
		try {
			const result = this.layer.requestPointerLock() as unknown as Promise<void> | undefined;
			if (result && result.catch) result.catch(() => {
				if (version === this.captureVersion) this.lockError();
			});
		} catch { this.lockError(); }
	}

	private syncCursor(): void {
		this.layer.style.cursor = this.host.enabled() ? (this.isLocked() ? 'none' : 'auto') : this.originalCursor;
	}

	key(code: number, down: boolean): boolean {
		if (!this.host.enabled()) return false;
		if (code === 27 && down) this.reset();
		if (![87, 65, 83, 68].includes(code)) return false;
		if (down && this.isLocked() && this.host.controllable()) this.keys.add(code);
		else this.keys.delete(code);
		this.sendMove();
		return true;
	}

	canAct(): boolean {
		return this.isLocked() && this.host.controllable();
	}

	private isLocked(): boolean {
		return document.pointerLockElement === this.layer;
	}

	private sendAim(force = false): void {
		const angle = Math.round(normalizeAngle(this.yaw)) % 360;
		if (angle === this.lastAim || (!force && Date.now() - this.lastAimTime < 25)) return;
		this.host.emit('a', this.host.gameId(), angle);
		this.lastAim = angle;
		this.lastAimTime = Date.now();
	}

	private sendMove(): void {
		const angle = movementAngle(this.yaw, Number(this.keys.has(87)) - Number(this.keys.has(83)),
			Number(this.keys.has(68)) - Number(this.keys.has(65)));
		const rounded = angle === null ? null : Math.round(angle) % 360;
		if (rounded === this.lastMove) return;
		this.moveGameId = this.host.gameId();
		this.host.emit('moveAngle', this.host.gameId(), rounded !== null, rounded === null ? undefined : rounded);
		this.lastMove = rounded;
	}

	private mouseMove = (event: MouseEvent): void => {
		if (!this.host.enabled() || !this.canAct()) return;
		this.yaw = normalizeAngle(this.yaw + event.movementX * 0.15);
		this.host.setYaw(this.yaw);
		this.sendAim();
		this.sendMove();
	};

	private mouseDown = (event: MouseEvent): void => {
		if (!this.host.enabled() || event.button !== 0 || !this.host.controllable()) return;
		if (!this.isLocked()) {
			this.requestCapture();
			return; // The click that captures the mouse never fires a weapon.
		}
		const player = this.host.player();
		if (!player) return;
		if (this.releaseTimer !== null) {
			window.clearTimeout(this.releaseTimer);
			this.releaseTimer = null;
			this.host.emit('m', this.actionGameId, '-l');
		}
		this.held = true;
		this.actionGameId = this.host.gameId();
		this.pressedAt = Date.now();
		this.sendAim(true);
		if (player.getWeapon() !== Weapon.Grenade && player.getWeapon() !== Weapon.Smoke) {
			this.host.emit('m', this.host.gameId(), 'l', aimPoint(player.getCenterX(), player.getCenterY(), this.yaw));
		}
	};

	private mouseUp = (event: MouseEvent): void => {
		if (event.button !== 0 || !this.held) return;
		this.held = false;
		const player = this.host.player();
		if (this.host.enabled() && this.canAct() && player
			&& (player.getWeapon() === Weapon.Grenade || player.getWeapon() === Weapon.Smoke)) {
			this.sendAim(true);
			this.host.emit('m', this.host.gameId(), 'l', aimPoint(player.getCenterX(), player.getCenterY(), this.yaw),
				// ThrowingObject uses 9 units per step; shorter distances round to zero steps.
				Math.max(9, Date.now() - this.pressedAt));
			if (this.releaseTimer !== null) window.clearTimeout(this.releaseTimer);
			this.releaseTimer = window.setTimeout(() => {
				this.host.emit('m', this.actionGameId, '-l');
				this.releaseTimer = null;
			}, 16);
		} else this.host.emit('m', this.host.gameId(), '-l');
	};

	private lockChange = (): void => {
		this.requestPending = false;
		const wasLocked = this.locked;
		this.locked = this.isLocked();
		if (!this.locked && wasLocked) this.reset();
		else if (this.locked && (!this.captureAllowed || !this.host.enabled() || (!this.starting && !this.host.controllable()))) this.reset();
		else if (this.locked) {
			this.layer.tabIndex = -1;
			this.layer.focus({ preventScroll: true });
		}
		this.syncCursor();
	};

	private lockError = (): void => {
		if (this.isLocked()) return;
		this.requestPending = false;
		this.captureAllowed = false;
		this.syncCursor();
		this.hint.textContent = 'Myš se nepodařilo zachytit. Klikni znovu, nebo použij ?renderer=2d.';
	};

	private visibilityChange = (): void => {
		if (document.hidden) this.reset();
	};

	reset = (): void => {
		this.captureVersion++;
		this.starting = false;
		this.autoCapturePending = false;
		this.captureAllowed = false;
		this.requestPending = false;
		this.clearControls();
		this.hint.style.display = 'none';
		if (this.isLocked()) document.exitPointerLock();
		this.syncCursor();
	};

	private clearControls(): void {
		if (this.lastMove !== null) this.host.emit('moveAngle', this.moveGameId, false);
		if (this.held || this.releaseTimer !== null) this.host.emit('m', this.actionGameId, '-l');
		if (this.releaseTimer !== null) window.clearTimeout(this.releaseTimer);
		this.releaseTimer = null;
		this.lastMove = null;
		this.held = false;
		this.keys.clear();
		this.lastAim = -1;
		this.playerId = null;
		this.host.setYaw(null);
	}

	dispose(): void {
		this.reset();
		this.layer.style.cursor = this.originalCursor;
		document.removeEventListener('mousemove', this.mouseMove);
		this.layer.removeEventListener('mousedown', this.mouseDown);
		document.removeEventListener('mouseup', this.mouseUp);
		document.removeEventListener('pointerlockchange', this.lockChange);
		document.removeEventListener('pointerlockerror', this.lockError);
		window.removeEventListener('blur', this.reset);
		document.removeEventListener('visibilitychange', this.visibilityChange);
		this.hint.remove();
	}
}
